import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppNamesQuery } from 'services/api/creativeServer';
import { Option } from '../types';

interface UseAppSearchProps {
  country: string;
  os: string;
}

export const useAppSearch = ({ country, os }: UseAppSearchProps) => {
  const [appSearchKeyword, setAppSearchKeyword] = useState('');
  const [appOptions, setAppOptions] = useState<Option[]>([]);
  const callbackRef = useRef<((options: Option[]) => void) | null>(null);

  const {
    data: appNamesData,
    isFetching: isAppFetching,
    isLoading: isAppLoading,
  } = useAppNamesQuery(
    {
      keyword: appSearchKeyword,
      country,
      os,
    },
    { skip: appSearchKeyword.trim().length === 0 },
  );

  useEffect(() => {
    if (callbackRef.current) {
      if (appNamesData?.appCountryProductList) {
        // remove duplicates
        const uniqueApps = appNamesData.appCountryProductList.filter(
          (app, index, self) =>
            index === self.findIndex(a => a.appIdStr === app.appIdStr),
        );

        const options = uniqueApps.map(app => ({
          value: app.appIdStr,
          label: `${app.appName}`,
          key: app.appIdStr,
        }));

        setAppOptions(options);
        callbackRef.current(options);
      } else if (appNamesData !== undefined) {
        // API call completed but no results
        setAppOptions([]);
        callbackRef.current([]);
      }
      callbackRef.current = null;
    }
  }, [appNamesData]);

  const loadAppOptions = useCallback(
    (inputValue: string, callback: (options: Option[]) => void) => {
      if (!inputValue) {
        callback([]);
        setAppOptions([]);
        return;
      }

      callbackRef.current = callback;
      setAppSearchKeyword(inputValue);
    },
    [],
  );

  return {
    loadAppOptions,
    isAppFetching,
    isAppLoading,
    appOptions,
  };
};
